import { castId } from '@studio/models';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { locationCache } from './constants.js';
import type { Room } from './types.js';
import { depot } from '#~/depot/index.js';
import type { Device } from '#~/device/index.js';

/**
 * Imperative function to locate a device record.
 *
 * @param deviceId - Device identifier.
 * @returns Device record or `undefined`.
 */
export function locateDevice(deviceId: Device['id']): T_Maybe<Device> {
  const room = locateRoom(deviceId);

  if (!room) {
    return undefined;
  }

  return room.devices[deviceId];
}

/**
 * Locate the room that contains the the specified device.
 *
 * @param id - Device or Room identifier.
 * @returns Room record or `undefined`.
 */
export function locateRoom(id: Device['id'] | Room['id']): T_Maybe<Room> {
  const { rooms } = depot.getState();

  // Were we given a room ID?
  let roomId: T_Maybe<Room['id']> = castId<Room['id']>(id);

  if (rooms[roomId]) {
    return rooms[roomId];
  }

  // Let's assume we were given a device ID.
  const deviceId = castId<Device['id']>(id);

  // Do we already which room contains this device?
  roomId = locationCache.get(deviceId);

  if (roomId) {
    const room = rooms[roomId];

    if (room) {
      return room;
    }

    locationCache.delete(deviceId);
    roomId = undefined;
  }

  // We don't know where this device is, so we'll have to search all of the rooms for it.
  for (const room of Object.values(depot.getState().rooms)) {
    if (room.devices[deviceId]) {
      roomId = room.id;

      locationCache.set(deviceId, roomId);

      break;
    }
  }

  return roomId ? rooms[roomId] : undefined;
}
